import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import axiosInstance from '../../api';

// ── Shared helpers ────────────────────────────────────────────────────────────

const fmt$ = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const INV_STATUS = {
  PAID:    { bg: '#d1fae5', text: '#065f46' },
  PENDING: { bg: '#fef3c7', text: '#92400e' },
  OVERDUE: { bg: '#fee2e2', text: '#991b1b' },
  VOID:    { bg: '#f1f5f9', text: '#64748b' },
};

const SUB_STATUS = {
  ACTIVE:    { bg: '#d1fae5', text: '#065f46' },
  TRIAL:     { bg: '#dbeafe', text: '#1e40af' },
  CANCELLED: { bg: '#f1f5f9', text: '#64748b' },
  EXPIRED:   { bg: '#fee2e2', text: '#991b1b' },
};

function Badge({ label, map }) {
  const s = (label || '').toUpperCase();
  const c = map[s] || { bg: '#f1f5f9', text: '#64748b' };
  return (
    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: c.bg, color: c.text, whiteSpace: 'nowrap' }}>
      {s}
    </span>
  );
}

function Pill({ color, children }) {
  return (
    <span style={{ background: color + '20', color, border: `1px solid ${color}50`, borderRadius: 6, fontSize: 11, fontWeight: 700, padding: '1px 8px' }}>
      {children}
    </span>
  );
}

// ── Shared modal shell ────────────────────────────────────────────────────────

function Modal({ title, icon, onClose, children, footer }) {
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <span style={S.modalTitle}><i className={`fas ${icon}`} style={{ marginRight: 8 }} />{title}</span>
          <button style={S.closeBtn} onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div style={S.modalBody}>{children}</div>
        {footer && <div style={S.modalFooter}>{footer}</div>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={S.label}>{label}</label>
      {children}
    </div>
  );
}

function Input(props) {
  return <input style={S.input} {...props} />;
}

function Select({ children, ...props }) {
  return <select style={S.input} {...props}>{children}</select>;
}

function Textarea(props) {
  return <textarea style={{ ...S.input, minHeight: 72, resize: 'vertical' }} {...props} />;
}

function SaveBtn({ loading, label, onClick }) {
  const { t } = useTranslation();
  return (
    <button style={S.saveBtn} onClick={onClick} disabled={loading}>
      {loading ? <><i className="fas fa-circle-notch fa-spin" style={{ marginRight: 6 }} />{t("AdminBillingPage.saving")}</> : (label != null ? label : t("AdminBillingPage.save"))}
    </button>
  );
}

// ── TAB: PRICING PLANS ────────────────────────────────────────────────────────

function PricingTab({ orgs, partners }) {
  const { t } = useTranslation();
  const [plans,   setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // null | 'create' | plan-object
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');

  const BLANK = { name: '', tier: 'STARTUP', framework: '', priceMonthly: '', priceAnnual: '', maxSubdomains: 50, maxFrameworks: 1, features: '', isActive: true };
  const [form, setForm] = useState(BLANK);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axiosInstance.get('/billing/admin/plans'); setPlans(r.data); }
    catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(BLANK); setErr(''); setModal('create'); };
  const openEdit   = (p) => {
    setForm({ ...p, features: p.featuresRaw || (p.features || []).join(', '), priceMonthly: p.priceMonthly, priceAnnual: p.priceAnnual || '' });
    setErr('');
    setModal(p);
  };

  const handleSave = async () => {
    if (!form.name || !form.priceMonthly) { setErr(t("AdminBillingPage.errNameMonthlyRequired")); return; }
    setSaving(true); setErr('');
    try {
      if (modal === 'create') {
        await axiosInstance.post('/billing/admin/plans', form);
      } else {
        await axiosInstance.put(`/billing/admin/plans/${modal.id}`, form);
      }
      setModal(null);
      load();
    } catch (e) {
      setErr(e.response?.data || e.message || t("AdminBillingPage.failedToSave"));
    } finally { setSaving(false); }
  };

  const handleDeactivate = async (plan) => {
    if (!window.confirm(t("AdminBillingPage.confirmDeactivatePlan", { name: plan.name }))) return;
    try { await axiosInstance.delete(`/billing/admin/plans/${plan.id}`); load(); }
    catch (e) { alert(e.message); }
  };

  const TIERS = ['STARTUP', 'GROWTH', 'ENTERPRISE', 'CUSTOM'];

  return (
    <>
      <div style={S.toolbar}>
        <span style={S.sectionTitle}><i className="fas fa-layer-group" style={{ marginRight: 8 }} />{t("AdminBillingPage.subscriptionPlans")}</span>
        <button style={S.addBtn} onClick={openCreate}><i className="fas fa-plus" style={{ marginRight: 6 }} />{t("AdminBillingPage.newPlan")}</button>
      </div>

      {loading ? <div style={S.center}><i className="fas fa-circle-notch fa-spin" style={{ fontSize: 24, color: '#2563eb' }} /></div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {plans.map(plan => (
            <div key={plan.id} style={{ ...S.card, borderTop: `4px solid ${plan.isActive ? '#2563eb' : '#e2e8f0'}`, opacity: plan.isActive ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <span style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{plan.name}</span>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <Pill color="#2563eb">{plan.tier}</Pill>
                    {plan.framework && <Pill color="#7c3aed">{plan.framework}</Pill>}
                    {!plan.isActive && <Pill color="#64748b">{t("AdminBillingPage.inactive")}</Pill>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={S.iconBtn} title={t("AdminBillingPage.edit")} onClick={() => openEdit(plan)}><i className="fas fa-edit" /></button>
                  {plan.isActive && <button style={{ ...S.iconBtn, color: '#dc2626' }} title={t("AdminBillingPage.deactivate")} onClick={() => handleDeactivate(plan)}><i className="fas fa-ban" /></button>}
                </div>
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#1e3a5f', marginBottom: 2 }}>
                {fmt$(plan.priceMonthly)}<span style={{ fontSize: 13, fontWeight: 400, color: '#64748b' }}>{t("AdminBillingPage.perMonth")}</span>
              </div>
              {plan.priceAnnual && (
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                  {t("AdminBillingPage.annualSaves", { price: fmt$(plan.priceAnnual), percent: Math.round((1 - plan.priceAnnual / (plan.priceMonthly * 12)) * 100) })}
                </div>
              )}
              <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
                {t("AdminBillingPage.planLimits", { subdomains: plan.maxSubdomains < 0 ? '∞' : plan.maxSubdomains, frameworks: plan.maxFrameworks < 0 ? '∞' : plan.maxFrameworks })}
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#475569', lineHeight: 1.8 }}>
                {(Array.isArray(plan.features) ? plan.features : []).slice(0, 4).map((f, i) => (
                  <li key={i}>{f.trim()}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal title={modal === 'create' ? t("AdminBillingPage.newPlan") : t("AdminBillingPage.editPlan")} icon="fa-layer-group" onClose={() => setModal(null)}
          footer={
            <>
              <button style={S.cancelBtn} onClick={() => setModal(null)}>{t("AdminBillingPage.cancel")}</button>
              <SaveBtn loading={saving} onClick={handleSave} />
            </>
          }>
          {err && <div style={S.errBox}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <Field label={t("AdminBillingPage.planNameLabel")}><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label={t("AdminBillingPage.tier")}>
              <Select value={form.tier} onChange={e => setForm(f => ({ ...f, tier: e.target.value }))}>
                {TIERS.map(t => <option key={t}>{t}</option>)}
              </Select>
            </Field>
            <Field label={t("AdminBillingPage.framework")}><Input placeholder={t("AdminBillingPage.frameworkPlaceholder")} value={form.framework || ''} onChange={e => setForm(f => ({ ...f, framework: e.target.value }))} /></Field>
            <Field label={t("AdminBillingPage.active")}>
              <Select value={form.isActive ? 'true' : 'false'} onChange={e => setForm(f => ({ ...f, isActive: e.target.value === 'true' }))}>
                <option value="true">{t("AdminBillingPage.yes")}</option>
                <option value="false">{t("AdminBillingPage.no")}</option>
              </Select>
            </Field>
            <Field label={t("AdminBillingPage.priceMonthlyLabel")}><Input type="number" value={form.priceMonthly} onChange={e => setForm(f => ({ ...f, priceMonthly: e.target.value }))} /></Field>
            <Field label={t("AdminBillingPage.priceAnnualLabel")}><Input type="number" value={form.priceAnnual || ''} onChange={e => setForm(f => ({ ...f, priceAnnual: e.target.value }))} /></Field>
            <Field label={t("AdminBillingPage.maxSubdomainsLabel")}><Input type="number" value={form.maxSubdomains} onChange={e => setForm(f => ({ ...f, maxSubdomains: e.target.value }))} /></Field>
            <Field label={t("AdminBillingPage.maxFrameworksLabel")}><Input type="number" value={form.maxFrameworks} onChange={e => setForm(f => ({ ...f, maxFrameworks: e.target.value }))} /></Field>
          </div>
          <Field label={t("AdminBillingPage.featuresLabel")}>
            <Textarea value={form.features || ''} onChange={e => setForm(f => ({ ...f, features: e.target.value }))} placeholder={t("AdminBillingPage.featuresPlaceholder")} />
          </Field>
        </Modal>
      )}
    </>
  );
}

// ── TAB: PROMO CODES ──────────────────────────────────────────────────────────

function PromoTab({ orgs, partners }) {
  const { t } = useTranslation();
  const [promos,  setPromos]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const BLANK = { code: '', description: '', discountType: 'PERCENTAGE', discountValue: '', maxUses: '', validFrom: '', validUntil: '', targetOrgId: '', targetPartnerId: '' };
  const [form, setForm] = useState(BLANK);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axiosInstance.get('/billing/admin/promo-codes'); setPromos(r.data); }
    catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.code || !form.discountValue) { setErr(t("AdminBillingPage.errCodeDiscountRequired")); return; }
    setSaving(true); setErr('');
    try {
      await axiosInstance.post('/billing/admin/promo-codes', {
        ...form,
        discountValue: form.discountValue,
        maxUses: form.maxUses || null,
        targetOrgId:     form.targetOrgId     || null,
        targetPartnerId: form.targetPartnerId || null,
        validFrom:  form.validFrom  ? form.validFrom + ':00'  : null,
        validUntil: form.validUntil ? form.validUntil + ':00' : null,
      });
      setModal(false);
      load();
    } catch (e) {
      setErr(e.response?.data || e.message || t("AdminBillingPage.failed"));
    } finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(t("AdminBillingPage.confirmDeactivatePromo", { code: p.code }))) return;
    try { await axiosInstance.delete(`/billing/admin/promo-codes/${p.id}`); load(); }
    catch (e) { alert(e.message); }
  };

  const now = LocalDateTimeNow();

  return (
    <>
      <div style={S.toolbar}>
        <span style={S.sectionTitle}><i className="fas fa-tag" style={{ marginRight: 8 }} />{t("AdminBillingPage.promoCodesAndDiscounts")}</span>
        <button style={S.addBtn} onClick={() => { setForm(BLANK); setErr(''); setModal(true); }}>
          <i className="fas fa-plus" style={{ marginRight: 6 }} />{t("AdminBillingPage.newPromo")}
        </button>
      </div>

      {loading ? <div style={S.center}><i className="fas fa-circle-notch fa-spin" style={{ fontSize: 24, color: '#2563eb' }} /></div> : (
        promos.length === 0 ? (
          <div style={S.empty}>
            <i className="fas fa-tag" style={{ fontSize: 32, color: '#cbd5e1', marginBottom: 12 }} />
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{t("AdminBillingPage.noPromoCodesYet")}</div>
            <div style={{ color: '#94a3b8', fontSize: 13 }}>{t("AdminBillingPage.noPromoCodesHint")}</div>
          </div>
        ) : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                {[
                  { k: 'code', label: t("AdminBillingPage.thCode") },
                  { k: 'type', label: t("AdminBillingPage.thType") },
                  { k: 'discount', label: t("AdminBillingPage.thDiscount") },
                  { k: 'uses', label: t("AdminBillingPage.thUses") },
                  { k: 'validUntil', label: t("AdminBillingPage.thValidUntil") },
                  { k: 'target', label: t("AdminBillingPage.thTarget") },
                  { k: 'status', label: t("AdminBillingPage.thStatus") },
                  { k: 'actions', label: '' },
                ].map(h => <th key={h.k} style={S.th}>{h.label}</th>)}
              </tr></thead>
              <tbody>
                {promos.map(p => {
                  const expired = p.validUntil && new Date(p.validUntil) < new Date();
                  const exhausted = p.maxUses && p.usesCount >= p.maxUses;
                  const active = p.isActive && !expired && !exhausted;
                  return (
                    <tr key={p.id} style={S.tr}>
                      <td style={S.td}><strong style={{ fontFamily: 'monospace', fontSize: 13 }}>{p.code}</strong></td>
                      <td style={S.td}><Pill color="#7c3aed">{p.discountType}</Pill></td>
                      <td style={S.td}>
                        <strong>{p.discountType === 'PERCENTAGE' ? `${p.discountValue}%` : fmt$(p.discountValue)}</strong>
                      </td>
                      <td style={S.td}>
                        <span style={{ color: exhausted ? '#dc2626' : '#334155' }}>
                          {p.usesCount}{p.maxUses ? ` / ${p.maxUses}` : ' / ∞'}
                        </span>
                      </td>
                      <td style={{ ...S.td, color: expired ? '#dc2626' : '#334155' }}>{p.validUntil ? fmtDate(p.validUntil) : '∞'}</td>
                      <td style={S.td}>
                        {p.targetOrgName && <span style={{ fontSize: 12, background: '#dbeafe', color: '#1e40af', padding: '1px 7px', borderRadius: 4 }}>{p.targetOrgName}</span>}
                        {p.targetPartnerName && <span style={{ fontSize: 12, background: '#faf5ff', color: '#6b21a8', padding: '1px 7px', borderRadius: 4 }}>{p.targetPartnerName}</span>}
                        {!p.targetOrgName && !p.targetPartnerName && <span style={{ color: '#94a3b8', fontSize: 12 }}>{t("AdminBillingPage.all")}</span>}
                      </td>
                      <td style={S.td}>
                        <Pill color={active ? '#16a34a' : '#dc2626'}>{active ? t("AdminBillingPage.statusActive") : expired ? t("AdminBillingPage.statusExpired") : exhausted ? t("AdminBillingPage.statusExhausted") : t("AdminBillingPage.statusInactive")}</Pill>
                      </td>
                      <td style={S.td}>
                        <button style={{ ...S.iconBtn, color: '#dc2626' }} onClick={() => handleDelete(p)}><i className="fas fa-trash-alt" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {modal && (
        <Modal title={t("AdminBillingPage.newPromoCode")} icon="fa-tag" onClose={() => setModal(false)}
          footer={
            <>
              <button style={S.cancelBtn} onClick={() => setModal(false)}>{t("AdminBillingPage.cancel")}</button>
              <SaveBtn loading={saving} label={t("AdminBillingPage.createPromo")} onClick={handleSave} />
            </>
          }>
          {err && <div style={S.errBox}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <Field label={t("AdminBillingPage.codeLabel")}>
              <Input placeholder="SUMMER25" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} />
            </Field>
            <Field label={t("AdminBillingPage.discountType")}>
              <Select value={form.discountType} onChange={e => setForm(f => ({ ...f, discountType: e.target.value }))}>
                <option value="PERCENTAGE">{t("AdminBillingPage.percentageOption")}</option>
                <option value="FIXED">{t("AdminBillingPage.fixedAmountOption")}</option>
              </Select>
            </Field>
            <Field label={form.discountType === 'PERCENTAGE' ? t("AdminBillingPage.discountValuePercentLabel") : t("AdminBillingPage.discountValueAmountLabel")}>
              <Input type="number" placeholder={form.discountType === 'PERCENTAGE' ? '20' : '100'} value={form.discountValue} onChange={e => setForm(f => ({ ...f, discountValue: e.target.value }))} />
            </Field>
            <Field label={t("AdminBillingPage.maxUsesLabel")}>
              <Input type="number" placeholder="∞" value={form.maxUses} onChange={e => setForm(f => ({ ...f, maxUses: e.target.value }))} />
            </Field>
            <Field label={t("AdminBillingPage.validFrom")}>
              <Input type="datetime-local" value={form.validFrom || now} onChange={e => setForm(f => ({ ...f, validFrom: e.target.value }))} />
            </Field>
            <Field label={t("AdminBillingPage.expiryDate")}>
              <Input type="datetime-local" value={form.validUntil} onChange={e => setForm(f => ({ ...f, validUntil: e.target.value }))} />
            </Field>
            <Field label={t("AdminBillingPage.targetOrgLabel")}>
              <Select value={form.targetOrgId} onChange={e => setForm(f => ({ ...f, targetOrgId: e.target.value }))}>
                <option value="">{t("AdminBillingPage.allOrgsOption")}</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name} (#{o.id})</option>)}
              </Select>
            </Field>
            <Field label={t("AdminBillingPage.targetPartnerLabel")}>
              <Select value={form.targetPartnerId} onChange={e => setForm(f => ({ ...f, targetPartnerId: e.target.value }))}>
                <option value="">{t("AdminBillingPage.allPartnersOption")}</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.firmName}</option>)}
              </Select>
            </Field>
          </div>
          <Field label={t("AdminBillingPage.description")}>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t("AdminBillingPage.promoDescriptionPlaceholder")} />
          </Field>
        </Modal>
      )}
    </>
  );
}

// ── TAB: SUBSCRIPTIONS ────────────────────────────────────────────────────────

function SubscriptionsTab() {
  const { t } = useTranslation();
  const [subs,    setSubs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axiosInstance.get('/billing/admin/subscriptions'); setSubs(r.data); }
    catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = subs.filter(s =>
    !search || s.orgName?.toLowerCase().includes(search.toLowerCase()) ||
    s.plan?.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div style={S.toolbar}>
        <span style={S.sectionTitle}><i className="fas fa-check-circle" style={{ marginRight: 8 }} />{t("AdminBillingPage.allSubscriptions")}</span>
        <input style={{ ...S.searchInput, marginLeft: 'auto' }} placeholder={t("AdminBillingPage.searchOrgOrPlan")} value={search} onChange={e => setSearch(e.target.value)} />
        <button style={S.refreshBtn} onClick={load} title={t("AdminBillingPage.refresh")}><i className="fas fa-sync-alt" /></button>
      </div>
      {loading ? <div style={S.center}><i className="fas fa-circle-notch fa-spin" style={{ fontSize: 24, color: '#2563eb' }} /></div> : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>
              {[
                { k: 'organisation', label: t("AdminBillingPage.thOrganisation") },
                { k: 'plan', label: t("AdminBillingPage.thPlan") },
                { k: 'cycle', label: t("AdminBillingPage.thCycle") },
                { k: 'status', label: t("AdminBillingPage.thStatus") },
                { k: 'started', label: t("AdminBillingPage.thStarted") },
                { k: 'renews', label: t("AdminBillingPage.thRenews") },
              ].map(h => <th key={h.k} style={S.th}>{h.label}</th>)}
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', color: '#94a3b8', padding: 40 }}>{t("AdminBillingPage.noSubscriptionsFound")}</td></tr>
              ) : filtered.map((s, i) => (
                <tr key={i} style={S.tr}>
                  <td style={S.td}>
                    <span style={{ fontWeight: 600 }}>{s.orgName}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', display: 'block' }}>{t("AdminBillingPage.idPrefix", { id: s.orgId })}</span>
                  </td>
                  <td style={S.td}>
                    <span style={{ fontWeight: 600 }}>{s.plan?.name}</span>
                    {s.plan?.tier && <Pill color="#2563eb" style={{ marginLeft: 6 }}>{s.plan.tier}</Pill>}
                  </td>
                  <td style={S.td}><Pill color="#0891b2">{s.billingCycle}</Pill></td>
                  <td style={S.td}><Badge label={s.status} map={SUB_STATUS} /></td>
                  <td style={{ ...S.td, color: '#64748b', fontSize: 12 }}>{fmtDate(s.startDate)}</td>
                  <td style={{ ...S.td, color: '#64748b', fontSize: 12 }}>{fmtDate(s.renewalDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── TAB: INVOICES ─────────────────────────────────────────────────────────────

function InvoicesTab({ orgs, partners }) {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axiosInstance.get('/billing/admin/invoices'); setInvoices(r.data); }
    catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleStatus = async (inv, newStatus) => {
    setUpdatingId(inv.id);
    try {
      const r = await axiosInstance.put(`/billing/admin/invoices/${inv.id}/status`, { status: newStatus });
      setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, ...r.data } : i));
    } catch (e) { alert(e.message); }
    finally { setUpdatingId(null); }
  };

  const filtered = invoices.filter(inv => {
    const matchStatus = !statusFilter || inv.status === statusFilter;
    const matchSearch = !search ||
      inv.orgName?.toLowerCase().includes(search.toLowerCase()) ||
      inv.invoiceNumber?.toLowerCase().includes(search.toLowerCase()) ||
      inv.partnerName?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const totals = invoices.reduce((a, i) => {
    a.total += Number(i.totalAmount) || 0;
    if (i.status === 'PAID')    a.paid    += Number(i.totalAmount) || 0;
    if (i.status === 'PENDING') a.pending += Number(i.totalAmount) || 0;
    return a;
  }, { total: 0, paid: 0, pending: 0 });

  return (
    <>
      <div style={S.toolbar}>
        <span style={S.sectionTitle}><i className="fas fa-file-invoice-dollar" style={{ marginRight: 8 }} />{t("AdminBillingPage.allInvoices")}</span>
        <input style={S.searchInput} placeholder={t("AdminBillingPage.searchInvoices")} value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...S.searchInput, maxWidth: 140 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">{t("AdminBillingPage.allStatus")}</option>
          {['PENDING', 'PAID', 'OVERDUE', 'VOID'].map(s => <option key={s}>{s}</option>)}
        </select>
        <button style={S.refreshBtn} onClick={load} title={t("AdminBillingPage.refresh")}><i className="fas fa-sync-alt" /></button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { label: t("AdminBillingPage.totalInvoiced"), value: fmt$(totals.total), color: '#2563eb' },
          { label: t("AdminBillingPage.paid"),          value: fmt$(totals.paid),    color: '#16a34a' },
          { label: t("AdminBillingPage.pending"),       value: fmt$(totals.pending), color: '#d97706' },
          { label: t("AdminBillingPage.invoices"),      value: invoices.length,      color: '#7c3aed' },
        ].map(c => (
          <div key={c.label} style={{ ...S.card, flex: 1, minWidth: 130, borderTop: `3px solid ${c.color}`, padding: '14px 18px' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {loading ? <div style={S.center}><i className="fas fa-circle-notch fa-spin" style={{ fontSize: 24, color: '#2563eb' }} /></div> : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>
              {[
                { k: 'invoiceNo', label: t("AdminBillingPage.thInvoiceNo") },
                { k: 'issuedBy', label: t("AdminBillingPage.thIssuedBy") },
                { k: 'organisation', label: t("AdminBillingPage.thOrganisation") },
                { k: 'partner', label: t("AdminBillingPage.thPartner") },
                { k: 'type', label: t("AdminBillingPage.thType") },
                { k: 'amount', label: t("AdminBillingPage.thAmount") },
                { k: 'discount', label: t("AdminBillingPage.thDiscount") },
                { k: 'tax', label: t("AdminBillingPage.thTax") },
                { k: 'total', label: t("AdminBillingPage.thTotal") },
                { k: 'status', label: t("AdminBillingPage.thStatus") },
                { k: 'due', label: t("AdminBillingPage.thDue") },
                { k: 'actions', label: t("AdminBillingPage.thActions") },
              ].map(h => <th key={h.k} style={S.th}>{h.label}</th>)}
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={12} style={{ ...S.td, textAlign: 'center', color: '#94a3b8', padding: 40 }}>{t("AdminBillingPage.noInvoicesFound")}</td></tr>
              ) : filtered.map(inv => (
                <tr key={inv.id} style={S.tr}>
                  <td style={S.td}><code style={{ fontSize: 12 }}>{inv.invoiceNumber || `INV-${inv.id}`}</code></td>
                  <td style={{ ...S.td, fontSize: 11 }}>
                    {inv.billingEntity === 'THEMARKUPS_CA' && <span title={t("AdminBillingPage.tmCanadaTitle")}>🇨🇦 {t("AdminBillingPage.tmCanada")}</span>}
                    {inv.billingEntity === 'THEMARKUPS_IN' && <span title={t("AdminBillingPage.tmIndiaTitle")}>🇮🇳 {t("AdminBillingPage.tmIndia")}</span>}
                    {!inv.billingEntity && <span style={{ color: '#94a3b8' }}>—</span>}
                    {inv.taxType && <div style={{ color: '#94a3b8', fontSize: 10 }}>{inv.taxType}{inv.clientProvince ? ` · ${inv.clientProvince}` : ''}</div>}
                  </td>
                  <td style={S.td}>
                    <span style={{ fontWeight: 600 }}>{inv.orgName}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', display: 'block' }}>{t("AdminBillingPage.idPrefix", { id: inv.orgId })}</span>
                  </td>
                  <td style={{ ...S.td, fontSize: 12 }}>{inv.partnerName || '—'}</td>
                  <td style={S.td}><Pill color="#0891b2">{inv.serviceType}</Pill></td>
                  <td style={{ ...S.td, fontSize: 12 }}>{fmt$(inv.amount)}</td>
                  <td style={{ ...S.td, fontSize: 12, color: inv.discountAmount > 0 ? '#16a34a' : '#94a3b8' }}>
                    {inv.discountAmount > 0 ? `-${fmt$(inv.discountAmount)}` : '—'}
                    {inv.promoCode && <span style={{ fontSize: 10, marginLeft: 4, fontFamily: 'monospace', background: '#f0fdf4', color: '#16a34a', padding: '0 4px', borderRadius: 3 }}>{inv.promoCode}</span>}
                  </td>
                  <td style={{ ...S.td, fontSize: 12, color: inv.taxAmount > 0 ? '#92400e' : '#94a3b8' }}>
                    {inv.taxAmount > 0 ? `+${fmt$(inv.taxAmount)}` : '—'}
                  </td>
                  <td style={{ ...S.td, fontWeight: 700 }}>{fmt$(inv.totalAmount)}</td>
                  <td style={S.td}><Badge label={inv.status} map={INV_STATUS} /></td>
                  <td style={{ ...S.td, fontSize: 12, color: '#64748b' }}>{fmtDate(inv.dueDate)}</td>
                  <td style={S.td}>
                    {updatingId === inv.id ? <i className="fas fa-circle-notch fa-spin" /> : (
                      <div style={{ display: 'flex', gap: 4 }}>
                        {inv.status !== 'PAID'  && <button style={{ ...S.miniBtn, background: '#d1fae5', color: '#065f46' }} onClick={() => handleStatus(inv, 'PAID')}>{t("AdminBillingPage.markPaid")}</button>}
                        {inv.status !== 'VOID'  && <button style={{ ...S.miniBtn, background: '#f1f5f9', color: '#64748b' }} onClick={() => handleStatus(inv, 'VOID')}>{t("AdminBillingPage.void")}</button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Canadian & Indian tax tables ──────────────────────────────────────────────

const CA_PROVINCES = [
  { name: 'Ontario',                   type: 'HST',     rate: 13,     gst: 13, pst: 0      },
  { name: 'Nova Scotia',               type: 'HST',     rate: 15,     gst: 15, pst: 0      },
  { name: 'New Brunswick',             type: 'HST',     rate: 15,     gst: 15, pst: 0      },
  { name: 'Prince Edward Island',      type: 'HST',     rate: 15,     gst: 15, pst: 0      },
  { name: 'Newfoundland and Labrador', type: 'HST',     rate: 15,     gst: 15, pst: 0      },
  { name: 'British Columbia',          type: 'GST+PST', rate: 12,     gst: 5,  pst: 7      },
  { name: 'Manitoba',                  type: 'GST+RST', rate: 12,     gst: 5,  pst: 7      },
  { name: 'Saskatchewan',              type: 'GST+PST', rate: 11,     gst: 5,  pst: 6      },
  { name: 'Quebec',                    type: 'GST+QST', rate: 14.975, gst: 5,  pst: 9.975  },
  { name: 'Alberta',                   type: 'GST',     rate: 5,      gst: 5,  pst: 0      },
  { name: 'Yukon',                     type: 'GST',     rate: 5,      gst: 5,  pst: 0      },
  { name: 'Northwest Territories',     type: 'GST',     rate: 5,      gst: 5,  pst: 0      },
  { name: 'Nunavut',                   type: 'GST',     rate: 5,      gst: 5,  pst: 0      },
];

const CA_TAX_MAP = Object.fromEntries(CA_PROVINCES.map(p => [p.name, p]));

function taxLabel(prov) {
  const p = CA_TAX_MAP[prov];
  if (!p) return '';
  if (p.type === 'HST')     return `HST ${p.rate}%`;
  if (p.type === 'GST')     return `GST ${p.rate}%`;
  return `GST ${p.gst}% + ${p.type.split('+')[1]} ${p.pst}% = ${p.rate}%`;
}

// ── TAB: GENERATE INVOICE ─────────────────────────────────────────────────────

const BILLING_ENTITIES = [
  {
    id:      'THEMARKUPS_CA',
    name:    'TheMarkups Inc.',
    sub:     'Ontario, Canada',
    flag:    '🇨🇦',
    color:   '#dc2626',
  },
  {
    id:      'THEMARKUPS_IN',
    name:    'TheMarkups Pvt Ltd',
    sub:     'India',
    flag:    '🇮🇳',
    color:   '#ea580c',
  },
];

function GenerateInvoiceTab() {
  const { t } = useTranslation();
  const [orgs,     setOrgs]     = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [loadErr,  setLoadErr]  = useState('');

  const [orgId,          setOrgId]          = useState('');
  const [partnerId,      setPartnerId]      = useState('');
  const [serviceType,    setServiceType]    = useState('SUBSCRIPTION');
  const [amount,         setAmount]         = useState('');
  const [dueDays,        setDueDays]        = useState('30');
  const [promoCode,      setPromoCode]      = useState('');
  const [description,    setDescription]    = useState('');
  const [billingEntity,  setBillingEntity]  = useState('THEMARKUPS_CA');
  const [clientProvince, setClientProvince] = useState('Ontario');
  const [indiaTaxType,   setIndiaTaxType]   = useState('IGST');

  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [err,    setErr]    = useState('');

  const SERVICE_TYPES = ['SUBSCRIPTION', 'AUDIT_FEE', 'SETUP_FEE', 'CONSULTING', 'SUPPORT', 'CUSTOM'];

  const loadRefData = useCallback(async () => {
    setLoading(true); setLoadErr('');
    try {
      const [oRes, pRes] = await Promise.all([
        axiosInstance.get('/billing/admin/organizations'),
        axiosInstance.get('/billing/admin/partners'),
      ]);
      setOrgs(oRes.data);
      setPartners(pRes.data);
    } catch (e) {
      setLoadErr(t("AdminBillingPage.failedLoadOrgsPartners") + ' ' + (e.message || ''));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { loadRefData(); }, [loadRefData]);

  // ── Tax derivation ──────────────────────────────────────────────
  const taxInfo = (() => {
    if (billingEntity === 'THEMARKUPS_CA') {
      const p = CA_TAX_MAP[clientProvince] || { type: 'GST', rate: 5, gst: 5, pst: 0 };
      return { rate: p.rate, type: p.type, label: taxLabel(clientProvince) };
    }
    if (indiaTaxType === 'CGST+SGST') return { rate: 18, type: 'CGST+SGST', label: 'CGST 9% + SGST 9% = 18%' };
    return { rate: 18, type: 'IGST', label: 'IGST 18%' };
  })();

  const numAmount   = parseFloat(amount) || 0;
  const taxAmount   = +(numAmount * taxInfo.rate / 100).toFixed(2);
  const totalAmount = +(numAmount + taxAmount).toFixed(2);

  const handleGenerate = async () => {
    if (!orgId)   { setErr(t("AdminBillingPage.errSelectOrg")); return; }
    if (!amount)  { setErr(t("AdminBillingPage.errAmountRequired")); return; }
    setSaving(true); setErr(''); setResult(null);
    try {
      const r = await axiosInstance.post('/billing/admin/invoices', {
        orgId,
        partnerId:      partnerId  || null,
        serviceType,
        amount,
        taxPercent:     taxInfo.rate,
        dueDays,
        promoCode:      promoCode  || null,
        description,
        billingEntity,
        taxType:        taxInfo.type,
        clientProvince: billingEntity === 'THEMARKUPS_CA' ? clientProvince : null,
      });
      setResult(r.data);
      setOrgId(''); setPartnerId(''); setAmount(''); setPromoCode(''); setDescription('');
    } catch (e) {
      setErr(e.response?.data || e.message || t("AdminBillingPage.failedGenerateInvoice"));
    } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: 740 }}>
      <div style={S.toolbar}>
        <span style={S.sectionTitle}><i className="fas fa-file-invoice" style={{ marginRight: 8 }} />{t("AdminBillingPage.generateInvoice")}</span>
      </div>

      {result && (
        <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 10, padding: '14px 20px', marginBottom: 20, color: '#065f46' }}>
          <i className="fas fa-check-circle" style={{ marginRight: 8 }} />
          {t("AdminBillingPage.invoiceGeneratedPrefix")} <strong>{result.invoiceNumber}</strong> {t("AdminBillingPage.invoiceGeneratedFor")} <strong>{result.orgName}</strong>
          {result.taxType && <span style={{ marginLeft: 6 }}>{t("AdminBillingPage.taxApplied", { taxType: result.taxType })}</span>}
          {' '}{t("AdminBillingPage.totalSuffix")} <strong>{fmt$(result.totalAmount)}</strong>
        </div>
      )}

      {err && <div style={S.errBox}><i className="fas fa-exclamation-circle" style={{ marginRight: 6 }} />{err}</div>}

      {/* ── Issuing Company ────────────────────────────── */}
      <div style={{ ...S.card, padding: '16px 20px', marginBottom: 18 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>
          {t("AdminBillingPage.issuingCompany")}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {BILLING_ENTITIES.map(e => {
            const active = billingEntity === e.id;
            return (
              <button key={e.id} onClick={() => setBillingEntity(e.id)}
                style={{ flex: 1, padding: '12px 16px', border: `2px solid ${active ? e.color : '#e2e8f0'}`,
                  borderRadius: 10, background: active ? e.color + '10' : '#fff', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 12, transition: 'all .15s' }}>
                <span style={{ fontSize: 26 }}>{e.flag}</span>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.875rem', color: active ? e.color : '#334155' }}>{e.name}</div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{e.sub}</div>
                </div>
                {active && <i className="fas fa-check-circle" style={{ marginLeft: 'auto', color: e.color }} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Tax Panel ─────────────────────────────────── */}
      <div style={{ ...S.card, padding: '16px 20px', marginBottom: 18, borderLeft: `4px solid ${billingEntity === 'THEMARKUPS_CA' ? '#dc2626' : '#ea580c'}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px', alignItems: 'end' }}>
          {billingEntity === 'THEMARKUPS_CA' ? (
            <>
              <Field label={t("AdminBillingPage.clientProvince")}>
                <Select value={clientProvince} onChange={e => setClientProvince(e.target.value)}>
                  {CA_PROVINCES.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                </Select>
              </Field>
              <div style={{ marginBottom: 14 }}>
                <label style={S.label}>{t("AdminBillingPage.taxAppliedCanadian")}</label>
                <div style={{ padding: '8px 12px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 7, fontSize: '0.875rem', color: '#92400e', fontWeight: 600 }}>
                  {taxLabel(clientProvince)}
                  <span style={{ fontWeight: 400, color: '#78350f', marginLeft: 8 }}>
                    — {CA_TAX_MAP[clientProvince]?.type === 'HST' ? t("AdminBillingPage.harmonisedSalesTax") :
                       CA_TAX_MAP[clientProvince]?.type === 'GST' ? t("AdminBillingPage.goodsAndServicesTax") :
                       t("AdminBillingPage.gstPlusProvincialTax")}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <Field label={t("AdminBillingPage.taxTypeIndiaGst")}>
                <Select value={indiaTaxType} onChange={e => setIndiaTaxType(e.target.value)}>
                  <option value="IGST">{t("AdminBillingPage.igstOption")}</option>
                  <option value="CGST+SGST">{t("AdminBillingPage.cgstSgstOption")}</option>
                </Select>
              </Field>
              <div style={{ marginBottom: 14 }}>
                <label style={S.label}>{t("AdminBillingPage.taxAppliedIndian")}</label>
                <div style={{ padding: '8px 12px', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 7, fontSize: '0.875rem', color: '#c2410c', fontWeight: 600 }}>
                  {taxInfo.label}
                  <span style={{ fontWeight: 400, color: '#9a3412', marginLeft: 8 }}>
                    — {indiaTaxType === 'IGST' ? t("AdminBillingPage.integratedGst") : t("AdminBillingPage.centralStateGst")}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Invoice Form ──────────────────────────────── */}
      <div style={{ ...S.card, padding: 24 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#64748b' }}>
            <i className="fas fa-circle-notch fa-spin" style={{ marginRight: 8 }} />{t("AdminBillingPage.loadingOrganisations")}
          </div>
        ) : loadErr ? (
          <div style={{ ...S.errBox, marginBottom: 16 }}>
            {loadErr}
            <button onClick={loadRefData} style={{ marginLeft: 'auto', fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>{t("AdminBillingPage.retry")}</button>
          </div>
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
          <Field label={t("AdminBillingPage.organisationRequired")}>
            <Select value={orgId} onChange={e => setOrgId(e.target.value)} disabled={loading}>
              <option value="">{t("AdminBillingPage.selectOption")}</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name} (#{o.id})</option>)}
            </Select>
          </Field>
          <Field label={t("AdminBillingPage.partnerOptional")}>
            <Select value={partnerId} onChange={e => setPartnerId(e.target.value)} disabled={loading}>
              <option value="">{t("AdminBillingPage.noneOption")}</option>
              {partners.map(p => <option key={p.id} value={p.id}>{p.firmName}</option>)}
            </Select>
          </Field>
          <Field label={t("AdminBillingPage.serviceType")}>
            <Select value={serviceType} onChange={e => setServiceType(e.target.value)}>
              {SERVICE_TYPES.map(t => <option key={t}>{t}</option>)}
            </Select>
          </Field>
          <Field label={t("AdminBillingPage.dueInDays")}>
            <Input type="number" value={dueDays} onChange={e => setDueDays(e.target.value)} />
          </Field>
          <Field label={t("AdminBillingPage.amountRequired")}>
            <Input type="number" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} />
          </Field>
          <Field label={t("AdminBillingPage.promoCode")}>
            <Input placeholder="SUMMER25" value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} />
          </Field>
        </div>

        <Field label={t("AdminBillingPage.description")}>
          <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={t("AdminBillingPage.invoiceDescriptionPlaceholder")} />
        </Field>

        {/* ── Amount Breakdown ─────────────────────────── */}
        {numAmount > 0 && (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>{t("AdminBillingPage.invoicePreview")}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569' }}>
                <span>{t("AdminBillingPage.subtotal")}</span><span>{fmt$(numAmount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#92400e' }}>
                <span>{taxInfo.label}</span><span>+{fmt$(taxAmount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1rem', color: '#0f172a', borderTop: '1px solid #e2e8f0', paddingTop: 6, marginTop: 4 }}>
                <span>{t("AdminBillingPage.total")}</span><span>{fmt$(totalAmount)}</span>
              </div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 4 }}>
                {t("AdminBillingPage.issuedByLabel")} {BILLING_ENTITIES.find(e => e.id === billingEntity)?.name} · {BILLING_ENTITIES.find(e => e.id === billingEntity)?.sub}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <SaveBtn loading={saving} label={t("AdminBillingPage.generateInvoice")} onClick={handleGenerate} />
        </div>
      </div>
    </div>
  );
}

// ── Utility ───────────────────────────────────────────────────────────────────

function LocalDateTimeNow() {
  const d = new Date();
  d.setSeconds(0, 0);
  return d.toISOString().slice(0, 16);
}

// ── Root component ────────────────────────────────────────────────────────────

export default function AdminBillingPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('pricing');
  const [orgs,     setOrgs]     = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    Promise.allSettled([
      axiosInstance.get('/billing/admin/organizations'),
      axiosInstance.get('/billing/admin/partners'),
    ]).then(([oRes, pRes]) => {
      if (oRes.status === 'fulfilled') setOrgs(oRes.value.data);
      if (pRes.status === 'fulfilled') setPartners(pRes.value.data);
    });
  }, []);

  const TABS = [
    { id: 'pricing',    icon: 'fa-layer-group',          label: t("AdminBillingPage.tabPricingPlans") },
    { id: 'promos',     icon: 'fa-tag',                  label: t("AdminBillingPage.tabPromoCodes") },
    { id: 'subs',       icon: 'fa-check-circle',         label: t("AdminBillingPage.tabSubscriptions") },
    { id: 'invoices',   icon: 'fa-file-invoice-dollar',  label: t("AdminBillingPage.tabInvoices") },
    { id: 'generate',   icon: 'fa-file-invoice',         label: t("AdminBillingPage.tabGenerateInvoice") },
  ];

  return (
    <div style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h2 style={S.pageTitle}><i className="fas fa-credit-card" style={{ marginRight: 10, color: '#2563eb' }} />{t("AdminBillingPage.billingAdministration")}</h2>
          <p style={S.pageSubtitle}>{t("AdminBillingPage.billingAdminSubtitle")}</p>
        </div>
      </div>

      <div style={S.tabBar}>
        {TABS.map(t => (
          <button key={t.id}
            style={{ ...S.tabBtn, ...(tab === t.id ? S.tabActive : {}) }}
            onClick={() => setTab(t.id)}>
            <i className={`fas ${t.icon}`} style={{ marginRight: 7 }} />{t.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 20 }}>
        {tab === 'pricing'  && <PricingTab   orgs={orgs} partners={partners} />}
        {tab === 'promos'   && <PromoTab     orgs={orgs} partners={partners} />}
        {tab === 'subs'     && <SubscriptionsTab />}
        {tab === 'invoices' && <InvoicesTab  orgs={orgs} partners={partners} />}
        {tab === 'generate' && <GenerateInvoiceTab />}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  page:        { padding: 24, background: '#f8fafc', minHeight: '100vh', fontFamily: 'Inter, sans-serif' },
  pageHeader:  { marginBottom: 22 },
  pageTitle:   { margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' },
  pageSubtitle:{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' },

  tabBar:    { display: 'flex', gap: 2, borderBottom: '2px solid #e2e8f0', flexWrap: 'wrap' },
  tabBtn:    { padding: '10px 16px', background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: -2, cursor: 'pointer', fontSize: '0.875rem', color: '#64748b', fontWeight: 500, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' },
  tabActive: { color: '#2563eb', borderBottomColor: '#2563eb', fontWeight: 700 },

  toolbar:     { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  sectionTitle:{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center' },
  addBtn:      { marginLeft: 'auto', padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' },
  refreshBtn:  { width: 34, height: 34, border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' },
  searchInput: { padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.8125rem', background: '#fff', minWidth: 180 },

  card:      { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', padding: 18 },
  tableWrap: { background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'auto' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' },
  th:        { padding: '9px 14px', textAlign: 'left', fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  td:        { padding: '11px 14px', color: '#334155', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  tr:        {},
  iconBtn:   { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '0.875rem', padding: '4px 6px', borderRadius: 4 },
  miniBtn:   { padding: '3px 10px', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' },

  center:    { display: 'flex', justifyContent: 'center', padding: '60px 0' },
  empty:     { background: '#fff', borderRadius: 10, padding: '60px 20px', textAlign: 'center', color: '#475569', border: '1px solid #e2e8f0' },

  // Modal
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal:     { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
  modalHeader:{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 },
  modalTitle: { fontSize: '1rem', fontWeight: 700, color: '#0f172a' },
  closeBtn:   { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1rem', padding: 4 },
  modalBody:  { padding: '20px 22px', overflowY: 'auto', flex: 1 },
  modalFooter:{ padding: '14px 22px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 },

  label:    { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 },
  input:    { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem', color: '#1e293b', background: '#fff', outline: 'none', boxSizing: 'border-box' },
  errBox:   { background: '#fee2e2', color: '#dc2626', padding: '9px 14px', borderRadius: 7, fontSize: '0.8125rem', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 },

  cancelBtn: { padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff', color: '#475569', fontSize: '0.875rem', cursor: 'pointer' },
  saveBtn:   { padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' },
};
